import { io, type Socket } from 'socket.io-client';
import type {
  AckResponse,
  ClientToServerEvents,
  ServerToClientEvents,
} from '@deck-after-dark/shared';

/** Client socket: listens for server events, emits client intents. */
export type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: GameSocket | null = null;

export function getServerUrl(): string | null {
  const url = import.meta.env.VITE_SERVER_URL?.trim();
  return url && url.length > 0 ? url : null;
}

export function isLiveMode(): boolean {
  return getServerUrl() !== null;
}

export function getGameSocket(): GameSocket {
  const url = getServerUrl();
  if (!url) throw new Error('VITE_SERVER_URL is not configured.');
  if (!socket) {
    socket = io(url, {
      transports: ['websocket'],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      ackTimeout: 10000,
      reconnectionDelay: 1000,
    });
  }
  return socket;
}

export function disconnectGameSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export async function connectGameSocket(): Promise<GameSocket> {
  const s = getGameSocket();
  if (s.connected) return s;
  return new Promise((resolve, reject) => {
    const onConnect = () => {
      cleanup();
      resolve(s);
    };
    const onError = (err: Error) => {
      cleanup();
      reject(err);
    };
    const cleanup = () => {
      s.off('connect', onConnect);
      s.off('connect_error', onError);
    };
    s.once('connect', onConnect);
    s.once('connect_error', onError);
    s.connect();
  });
}

export function unwrapAck<T>(res: AckResponse<T>): T {
  if (!res.ok) throw new Error(`${res.error.code}: ${res.error.message}`);
  return res.data;
}
